/**
 * Product Card Renderer Module
 * Renders B2B product cards with variants and advanced options.
 */

/**
 * Extracts numeric price from formatted money string.
 * @param {string} priceString - The formatted price string (e.g., "£10.50").
 * @returns {number} The numeric price value.
 */
function extractPrice(priceString) {
  if (!priceString || priceString === "N/A") {
    return 0;
  }
  return parseFloat(priceString.replace(/[^\d.,]/g, "").replace(",", ".")) || 0;
}

/**
 * Builds responsive image URL with width parameter.
 * @param {string} imageUrl - The base image URL.
 * @param {number} width - The desired width in pixels.
 * @returns {string} The image URL with width parameter.
 */
function buildImageUrl(imageUrl, width) {
  // Return data URIs as-is since they can't have query parameters.
  if (imageUrl && imageUrl.startsWith("data:")) {
    return imageUrl;
  }
  if (imageUrl.includes("width=")) {
    return imageUrl.replace(/width=\d+/, `width=${width}`);
  }
  const separator = imageUrl.includes("?") ? "&" : "?";
  return `${imageUrl}${separator}width=${width}`;
}

/**
 * Generates HTML for a single product variant card.
 * @param {Object} options - The options object.
 * @param {Object} options.product - The product object.
 * @param {number} options.price - The product price.
 * @param {number} options.unitPrice - The product unit price.
 * @param {Object} options.unitPriceMeasurement - The product unit price measurement.
 * @param {Object} options.variant - The variant object.
 * @param {string} options.productUrl - The product URL.
 * @param {string} options.productTitleId - The product title element ID.
 * @param {string} options.badgeId - The badge element ID.
 * @param {number} options.caseQty - The case quantity.
 * @param {string} options.displayUnitPackSize - The unit pack size to display.
 * @param {number} options.recommendedRetailPriceNumeric - The recommended retail price as a number.
 * @param {boolean} options.isVatMessaging - Whether the product has VAT messaging.
 * @param {boolean} options.isRestricted - Whether the product is restricted.
 * @returns {string} The HTML string for the variant card.
 */
function renderVariantCard({
  product,
  price,
  unitPrice,
  unitPriceMeasurement,
  variant,
  productUrl,
  productTitleId,
  badgeId,
  caseQty,
  displayUnitPackSize,
  recommendedRetailPriceNumeric,
  isVatMessaging,
  isRestricted,
}) {
  const variantTitle = variant.title !== "Default Title" ? variant.title : "";
  const advancedOptionsId = `AdvancedOptions-${variant.id}`;
  const quantityInputId = `quantity-${variant.id}`;
  const isInStock = variant.inventory > 0;
  const productId = product.id;

  // Use passed numeric values, or extract from formatted strings as fallback.
  const priceInput =
    price || extractPrice(variant.price || product.price || "0");
  const unitPriceInput =
    unitPrice ||
    extractPrice(variant.unit_price || product.unit_price || "0") ||
    null;

  const unitPriceMeasurementInput =
    unitPriceMeasurement || variant.unit_price_measurement || null;
  // Build responsive image srcset.
  const imageSrcset = `${buildImageUrl(product.image, 165)} 165w, ${buildImageUrl(
    product.image,
    360,
  )} 360w, ${buildImageUrl(product.image, 533)} 533w, ${buildImageUrl(
    product.image,
    720,
  )} 720w`;

  let productPlaceholderImage = null;

  if (product.image.includes("data:")) {
    productPlaceholderImage = true;
  } else {
    productPlaceholderImage = false;
  }

  return `
      <div class="card-wrapper product-card-wrapper underline-links-hover">
        <div class="card card--card card--media color-scheme-2 gradient ${productPlaceholderImage ? "card--placeholder-image" : ""}" style="--ratio-percent: 100.0%;">
          <div class="card__inner ratio" style="--ratio-percent: 100.0%;">
            <div class="card__media">
              <div class="media media--transparent media--hover-effect">
                <img
                  src="${product.image}"
                  srcset="${imageSrcset}"
                  sizes="(min-width: 1200px) 267px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                  alt="${product.title}"
                  class="motion-reduce"
                  loading="lazy"
                >
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading">
                  <a href="${productUrl}" id="${productTitleId}" class="full-unstyled-link" aria-labelledby="${productTitleId} ${badgeId}">
                    ${product.title}
                  </a>
                </h3>
              </div>
                ${
                  isInStock
                    ? `<div class="card__badge top left"><span id="${badgeId}-bottom" class="badge badge--top-left color-scheme-5">${window.advancedOptionsCardTranslations.inStock}</span></div>`
                    : `<div class="card__badge top left"><span id="${badgeId}-bottom" class="badge badge--top-left color-scheme-5 badge--out-of-stock">${window.advancedOptionsCardTranslations.outOfStock}</span></div>`
                }
            </div>
          </div>
            <div class="card__content">
            <div class="card__information">
              <div class="card__information-sku">
                <span class="visually-hidden">${window.advancedOptionsCardTranslations.skuLabel}</span>${variant.sku || "N/A"}
              </div>
              <h3 class="card__heading h3" id="title-${variant.id}">
                <a href="${productUrl}" id="${productTitleId}-variant" class="full-unstyled-link" aria-labelledby="${productTitleId}-variant ${badgeId}">
                  ${product.title}${variantTitle ? ` - ${variantTitle}` : ""}
                </a>
              </h3>
              <div class="card__information-price-pack-size">
                ${
                  displayUnitPackSize
                    ? `<span class="caption-large light">${window.advancedOptionsCardTranslations.unitPackSize}<span class="caption-large-bold">${displayUnitPackSize}</span></span>`
                    : ""
                }
                ${
                  recommendedRetailPriceNumeric &&
                  recommendedRetailPriceNumeric !== 0
                    ? `<span class="caption-large light">${window.advancedOptionsCardTranslations.rrp}<span class="caption-large-bold">${window.advancedOptionsCardTranslations.currencySymbol || "£"}${recommendedRetailPriceNumeric.toFixed(2)}</span></span>`
                    : ""
                }
              </div>
            </div>
            <div class="quick-add no-js-hidden">
              <advanced-options-card
                id="${advancedOptionsId}"
                class="advanced-options-card"
                data-case-quantity="${caseQty}"
                data-case-price="${priceInput}"
                data-recommended-retail-price="${recommendedRetailPriceNumeric}"
                data-variant-barcode="${variant.barcode}"
                data-variant-sku="${variant.sku}"
                data-variant-case-qty="${unitPriceMeasurementInput}"
                data-variant-unit-price="${unitPriceInput ? unitPriceInput.toFixed(2) : "N/A"}"
                data-vat-messaging="${isVatMessaging}"
              >
              <div class="advanced-options-card__pricing">
                <table class="advanced-options-card__table">
                  <thead class="advanced-options-card__table-header">
                    <tr class="advanced-options-card__table-row">
                      <th class="advanced-options-card__table-cell advanced-options-card__table-cell--header">${window.advancedOptionsCardTranslations.caseQty}</th>
                      <th class="advanced-options-card__table-cell advanced-options-card__table-cell--header">${window.advancedOptionsCardTranslations.unitPrice}</th>
                      <th class="advanced-options-card__table-cell advanced-options-card__table-cell--header">${window.advancedOptionsCardTranslations.casePrice}</th>
                    </tr>
                  </thead>
                  <tbody class="advanced-options-card__table-body">
                    <tr class="advanced-options-card__table-row">
                      <td class="advanced-options-card__table-cell">
                        ${caseQty}
                      </td>
                      <td class="advanced-options-card__table-cell">
                        ${unitPriceInput ? `${window.advancedOptionsCardTranslations.currencySymbol || "£"}${unitPriceInput.toFixed(2)}` : "N/A"}
                      </td>
                      <td class="advanced-options-card__table-cell advanced-options-card__table-cell--case-price">
                        ${window.advancedOptionsCardTranslations.currencySymbol || "£"}${priceInput.toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table> 
              </div>          
                <div class="advanced-options-card__order">
                  <div class="advanced-options-card__summary">
                    <div class="advanced-options-card__summary-item">
                      <span class="advanced-options-card__summary-label visually-hidden" aria-label="${window.advancedOptionsCardTranslations.totalPrice.replace(":", "")}" aria-hidden="true">${window.advancedOptionsCardTranslations.totalPrice}</span>
                      <span class="advanced-options-card__summary-value" id="total-price-${productId}">${window.advancedOptionsCardTranslations.currencySymbol || "£"}${priceInput.toFixed(2)}</span>
                      ${isVatMessaging ? `<span class="advanced-options-card__summary-value">${window.advancedOptionsCardTranslations.exVat}</span>` : ""}
                    </div>
                  </div>
                  <div class="advanced-options-card__controls">
                    <div class="advanced-options-card__quantity">
                      <div class="advanced-options-card__quantity-selector">
                        <button type="button" class="advanced-options-card__quantity-btn advanced-options-card__quantity-btn--decrease" data-action="decrease" ${!isInStock || isRestricted ? "disabled" : ""}>
                          <span class="visually-hidden">${window.advancedOptionsCardTranslations.decreaseQuantity}</span>
                          −
                        </button>
                        <input type="number" id="${quantityInputId}" class="advanced-options-card__quantity-input" value="1" min="1" max="999" data-product-id="${productId}" ${!isInStock || isRestricted ? "disabled" : ""}>
                        <button type="button" class="advanced-options-card__quantity-btn advanced-options-card__quantity-btn--increase" data-action="increase" ${!isInStock || isRestricted ? "disabled" : ""}>
                          <span class="visually-hidden">${window.advancedOptionsCardTranslations.increaseQuantity}</span>
                          +
                        </button>
                      </div>
                      ${
                        isInStock
                          ? `<button type="button" class="advanced-options-card__buy-btn button button--primary button--blue" ${isRestricted ? "disabled" : ""} data-product-id="${productId}" data-variant-id="${variant.id}" data-ignore-cart-notification="true"> ${isRestricted ? window.advancedOptionsCardTranslations.notAvailableInIreland : window.advancedOptionsCardTranslations.buyNow}</button>`
                          : `<button type="button" class="advanced-options-card__buy-btn button button--primary button--blue" disabled data-product-id="${productId}" data-variant-id="${variant.id}" data-ignore-cart-notification="true"> ${isRestricted ? window.advancedOptionsCardTranslations.notAvailableInIreland : window.advancedOptionsCardTranslations.outOfStock}</button>`
                      }
                    </div>
                  </div>
                </div>
              </advanced-options-card>
            </div>
          </div>
        </div>
      </div>
    `;
}

/**
 * Renders product cards for all variants of a product.
 * @param {Object} options - The options object.
 * @param {Object} options.product - The product object with variants array.
 * @param {string|HTMLElement} [options.container] - The container selector or element to render into. If not provided, returns HTML string.
 * @param {Function} [options.onProductStored] - Callback function called with the product object after rendering.
 * @returns {string} The HTML string containing all variant cards.
 */
export function renderProductCards({
  product,
  container = null,
  onProductStored = null,
}) {
  // Early return if product is invalid.
  if (!product || !product.variants || product.variants.length === 0) {
    return "";
  }

  const productUrl = `/products/${product.handle}`;
  const productId = product.id;
  const timestamp = Date.now();
  const productTitleId = `CardLink-${productId}-${timestamp}`;
  const badgeId = `Badge-${productId}-${timestamp}`;

  // Extract metafield data.
  const metafields = product.metafields || {};
  const unitPackSizes = metafields.pdp_unit_pack_sizes || "";
  const pdpCaseQuantity = metafields.pdp_case_quantity || null;
  const pdpRecommendedRetailPrice = metafields.recommended_retail_price || null;
  const excludeIreland = metafields.exclude_ireland || null;
  const vatMessaging = metafields.vat_messaging || null;
  const productTags = product.tags || [];
  const hasExcludeIrelandTag =
    Array.isArray(productTags) && productTags.includes("exclude_ireland");
  const productHasExcludeIreland =
    excludeIreland === true || excludeIreland === "true" || hasExcludeIrelandTag;

  // Compute restriction using same logic as product-restriction-logic.liquid.
  const context = window.productRestrictionContext || {};
  const isRestrictedForProduct = (() => {
    if (!context.enabled || !productHasExcludeIreland) return false;
    if (context.isIrelandMarket) return true;
    const prov = (context.customerProvince || "").toLowerCase();
    const count = (context.customerCountry || "").toLowerCase();
    return (
      (prov && prov.includes("ireland")) || (count && count.includes("ireland"))
    );
  })();

  // Generate HTML for all variants.
  const cardsHtml = product.variants
    .map((variant) => {
      const variantTitle =
        variant.title !== "Default Title" ? variant.title : "";
      const caseQty = pdpCaseQuantity || variant.case_qty || 1;
      const recommendedRetailPrice =
        pdpRecommendedRetailPrice || variant.recommended_retail_price || "N/A";
      const displayUnitPackSize = unitPackSizes || variantTitle;
      const recommendedRetailPriceNumeric = extractPrice(
        recommendedRetailPrice,
      );
      const isRestricted = isRestrictedForProduct;
      const isVatMessaging = vatMessaging || false;
      // Extract numeric prices from formatted money strings.
      const price = extractPrice(variant.price || product.price || "0");
      const unitPrice = variant.unit_price
        ? extractPrice(variant.unit_price)
        : null;
      const unitPriceMeasurement = variant.unit_price_measurement
        ? variant.unit_price_measurement
        : null;

      return renderVariantCard({
        product,
        price,
        unitPrice,
        unitPriceMeasurement,
        variant,
        productUrl,
        productTitleId,
        badgeId,
        caseQty,
        displayUnitPackSize,
        recommendedRetailPriceNumeric,
        isVatMessaging,
        isRestricted,
      });
    })
    .join("");

  // Render to container if provided.
  if (container) {
    const containerElement =
      typeof container === "string"
        ? document.querySelector(container) ||
          document.getElementById(container)
        : container;

    if (containerElement) {
      containerElement.innerHTML = cardsHtml;
    }
  }

  // Call callback if provided.
  if (onProductStored && typeof onProductStored === "function") {
    onProductStored(product);
  }

  return cardsHtml;
}
